import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Table, Card, Spin, Row, Col, Typography, Alert, DatePicker, Select, Space, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { getKpi } from '../api';
import { TZ } from '../../../utils/tz';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Названия месяцев
const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

// Форматирование валюты
const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT', maximumFractionDigits: 0 }).format(value);
};

// Форматирование процента
const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${value.toFixed(2)}%`;
};

// Расчёт конверсий
const calculateConversions = (data) => {
    const leads = Number(data.leads || 0);
    const qualified = Number(data.qualified || 0);
    const measure = Number(data.measure || 0);
    const salesCount = Number(data.salesCount || 0);
    const salesSum = Number(data.salesSum || 0);

    return {
        leads,
        qualified,
        measure,
        salesCount,
        salesSum,
        avgCheck: salesCount > 0 ? Math.round(salesSum / salesCount) : 0,
        leadToQual: leads > 0 ? (qualified / leads) * 100 : 0,
        qualToMeasure: qualified > 0 ? (measure / qualified) * 100 : 0,
        measureToSale: measure > 0 ? (salesCount / measure) * 100 : 0,
        leadToSale: leads > 0 ? (salesCount / leads) * 100 : 0,
    };
};

// Порядок менеджеров
const MANAGER_ORDER = ['жасулан', 'алибек', 'санат', 'арай', 'алишер', 'мариям'];

const sortManagers = (managers) => {
    return [...managers].sort((a, b) => {
        const getIndex = (name) => {
            const lower = (name || '').toLowerCase();
            const index = MANAGER_ORDER.findIndex(key => lower.includes(key));
            return index === -1 ? 999 : index;
        };
        return getIndex(a.name) - getIndex(b.name);
    });
};

const getFirstName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    return parts.length > 1 ? parts[1] : parts[0];
};

/**
 * Компонент отображения конверсий менеджеров
 */
const ConversionReport = ({ managers = [], plans = {}, kpi = {}, currency = 'KZT', dateRange }) => {
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [currentMonthData, setCurrentMonthData] = useState({});
    const [historyData, setHistoryData] = useState({});

    // Минимальная дата для выбора (данные доступны с августа 2025)
    const MIN_DATA_DATE = moment('2025-08-01');

    // Период для исторической динамики (по умолчанию - август 2025 - текущий месяц)
    const [historyRange, setHistoryRange] = useState([
        moment('2025-08-01').startOf('month'),
        moment().endOf('month')
    ]);

    // Выбранный менеджер для исторической динамики
    const [selectedManagerId, setSelectedManagerId] = useState(null);

    // Загрузка данных за выбранный период (из dateRange)
    useEffect(() => {
        const loadPeriodData = async () => {
            setLoading(true);
            try {
                // Используем dateRange если передан, иначе текущий месяц
                let from, to;
                if (dateRange && dateRange[0] && dateRange[1]) {
                    from = dateRange[0].clone().startOf('day').toISOString();
                    to = dateRange[1].clone().endOf('day').toISOString();
                } else {
                    from = moment().startOf('month').toISOString();
                    to = moment().endOf('month').toISOString();
                }

                console.log('[ConversionReport] Loading data for period:', { from, to });

                const kpiData = await getKpi({ from, to, includeCalls: false });

                if (kpiData && kpiData.managers) {
                    const data = {};
                    kpiData.managers.forEach(manager => {
                        data[manager.id] = calculateConversions(manager);
                    });
                    setCurrentMonthData(data);
                    console.log('[ConversionReport] Loaded managers data:', Object.keys(data).length, 'managers');
                }
            } catch (e) {
                console.error('[ConversionReport] Failed to load period data', e);
            }
            setLoading(false);
        };

        loadPeriodData();
    }, [dateRange]);

    // Загрузка исторических данных за выбранный период
    const loadHistoryData = useCallback(async () => {
        if (!historyRange || !historyRange[0] || !historyRange[1]) return;

        setHistoryLoading(true);
        const data = {};

        try {
            // Генерируем список месяцев в выбранном диапазоне
            const startMonth = historyRange[0].clone().startOf('month');
            const endMonth = historyRange[1].clone().endOf('month');
            const months = [];

            let current = startMonth.clone();
            while (current.isSameOrBefore(endMonth, 'month')) {
                months.push({
                    key: current.format('YYYY-MM'),
                    label: MONTHS[current.month()],
                    year: current.year(),
                    month: current.month()
                });
                current.add(1, 'month');
            }

            // Загружаем данные за каждый месяц
            for (const m of months) {
                try {
                    const from = moment(`${m.year}-${String(m.month + 1).padStart(2, '0')}-01`).startOf('month').toISOString();
                    const to = moment(`${m.year}-${String(m.month + 1).padStart(2, '0')}-01`).endOf('month').toISOString();

                    console.log(`[ConversionReport] Loading history for ${m.key}:`, { from, to });

                    const kpiData = await getKpi({ from, to, includeCalls: false });

                    if (kpiData && kpiData.managers) {
                        data[m.key] = {
                            label: m.label,
                            managers: {}
                        };
                        kpiData.managers.forEach(manager => {
                            data[m.key].managers[manager.id] = calculateConversions(manager);
                        });
                    }
                } catch (e) {
                    console.error(`[ConversionReport] Failed to load data for ${m.key}`, e);
                }
            }
        } catch (e) {
            console.error('[ConversionReport] Failed to load history data', e);
        }

        setHistoryData(data);
        setHistoryLoading(false);
    }, [historyRange]);

    // Загружаем исторические данные при изменении периода
    useEffect(() => {
        loadHistoryData();
    }, [loadHistoryData]);

    // Сортированные менеджеры
    const sortedManagers = useMemo(() => sortManagers(managers), [managers]);

    // Генерация строк для исторической таблицы
    const generateHistoricalRows = useCallback((managerId) => {
        const rows = [
            { key: 'leads', label: 'ЛИДЫ', isAbsolute: true },
            { key: 'qualified', label: 'КВАЛ', isAbsolute: true },
            { key: 'measure', label: 'ЗАМЕР', isAbsolute: true },
            { key: 'salesCount', label: 'ПРОДАЖА', isAbsolute: true },
            { key: 'salesSum', label: 'СУММА', isAbsolute: true, isMoney: true },
            { key: 'avgCheck', label: 'СР ЧЕК', isAbsolute: true, isMoney: true },
            { key: 'leadToQual', label: 'лид - квал', isPercent: true },
            { key: 'qualToMeasure', label: 'квал - замер', isPercent: true },
            { key: 'measureToSale', label: 'замер - продажа', isPercent: true },
            { key: 'leadToSale', label: 'лид - продажа', isPercent: true },
        ];

        const monthKeys = Object.keys(historyData).sort();

        return rows.map(row => {
            const dataRow = { key: row.key, metric: row.label, isPercent: row.isPercent, isMoney: row.isMoney };

            monthKeys.forEach(monthKey => {
                const managerData = historyData[monthKey]?.managers?.[managerId];
                dataRow[monthKey] = managerData?.[row.key] ?? null;
            });

            return dataRow;
        });
    }, [historyData]);

    // Колонки для исторической таблицы
    const historicalColumns = useMemo(() => {
        const monthKeys = Object.keys(historyData).sort();

        const cols = [
            {
                title: '',
                dataIndex: 'metric',
                key: 'metric',
                fixed: 'left',
                width: 120,
                render: (text) => <Text strong style={{ fontSize: '11px' }}>{text}</Text>
            }
        ];

        monthKeys.forEach(monthKey => {
            const monthLabel = historyData[monthKey]?.label || monthKey;
            cols.push({
                title: <span style={{ fontSize: '11px' }}>{monthLabel}</span>,
                dataIndex: monthKey,
                key: monthKey,
                width: 100,
                align: 'right',
                render: (value, record) => {
                    if (value === null || value === undefined) return <span style={{ color: '#d9d9d9' }}>-</span>;

                    if (record.isPercent) {
                        return <span style={{ fontSize: '11px' }}>{formatPercent(value)}</span>;
                    }
                    if (record.isMoney) {
                        return <span style={{ fontSize: '11px' }}>{formatCurrency(value)}</span>;
                    }
                    return <span style={{ fontSize: '11px' }}>{value}</span>;
                }
            });
        });

        return cols;
    }, [historyData]);

    // Расчёт плановых конверсий
    const calculatePlanConversions = (managerPlan) => {
        if (!managerPlan?.derived) return {};

        const leads = Number(managerPlan.derived.leadsPlan || managerPlan.leadsPlan || 0);
        const qualified = Number(managerPlan.derived.qualifiedPlan || managerPlan.qualifiedPlan || 0);
        const measure = Number(managerPlan.derived.measurePlan || managerPlan.measurePlan || 0);
        const salesCount = Number(managerPlan.derived.salesCountPlan || managerPlan.salesCountPlan || 0);

        return {
            leadToQualPlan: leads > 0 ? (qualified / leads) * 100 : null,
            qualToMeasurePlan: qualified > 0 ? (measure / qualified) * 100 : null,
            measureToSalePlan: measure > 0 ? (salesCount / measure) * 100 : null,
            leadToSalePlan: leads > 0 ? (salesCount / leads) * 100 : null,
        };
    };

    // Данные для таблицы текущего месяца (План/Факт)
    const currentMonthTableData = useMemo(() => {
        const rows = [
            { key: 'leads', label: 'ЛИДЫ', planKey: 'leadsPlan' },
            { key: 'qualified', label: 'КВАЛ', planKey: 'qualifiedPlan' },
            { key: 'measure', label: 'ЗАМЕР', planKey: 'measurePlan' },
            { key: 'salesCount', label: 'ПРОДАЖА', planKey: 'salesCountPlan' },
            { key: 'salesSum', label: 'СУММА', planKey: 'salesSumPlan', isMoney: true },
            { key: 'avgCheck', label: 'СР ЧЕК', planKey: 'avgCheckPlan', isMoney: true },
            { key: 'leadToQual', label: 'лид - квал', isPercent: true, planKey: 'leadToQualPlan' },
            { key: 'qualToMeasure', label: 'квал - замер', isPercent: true, planKey: 'qualToMeasurePlan' },
            { key: 'measureToSale', label: 'замер - продажа', isPercent: true, planKey: 'measureToSalePlan' },
            { key: 'leadToSale', label: 'лид - продажа', isPercent: true, planKey: 'leadToSalePlan' },
        ];

        return rows.map(row => {
            const dataRow = { key: row.key, metric: row.label, isPercent: row.isPercent, isMoney: row.isMoney };

            sortedManagers.forEach(m => {
                // Приоритет: 1) kpi из props (актуальные данные за выбранный период)
                //            2) currentMonthData (локально загруженные данные)
                const kpiManager = (kpi.managers || []).find(km => km.id === m.id);
                const localData = currentMonthData[m.id];

                // Используем данные из kpi props, если есть, иначе локальные
                const managerData = kpiManager
                    ? calculateConversions(kpiManager)
                    : localData;

                const managerPlan = plans.managers?.find(p => p.id === m.id);
                const planConversions = calculatePlanConversions(managerPlan);

                let planValue = null;
                if (row.planKey) {
                    // Сначала проверяем расчётные конверсии
                    if (planConversions[row.planKey] !== undefined) {
                        planValue = planConversions[row.planKey];
                    } else if (managerPlan?.derived?.[row.planKey] !== undefined) {
                        planValue = managerPlan.derived[row.planKey];
                    } else if (managerPlan?.[row.planKey] !== undefined) {
                        // Fallback на корневой уровень (для avgCheckPlan)
                        planValue = managerPlan[row.planKey];
                    }
                }

                const factValue = managerData?.[row.key] ?? null;

                dataRow[`${m.id}_plan`] = planValue;
                dataRow[`${m.id}_fact`] = factValue;
            });

            return dataRow;
        });
    }, [currentMonthData, sortedManagers, plans, kpi]);

    // Колонки для таблицы текущего месяца
    const currentMonthColumns = useMemo(() => {
        const cols = [
            {
                title: '',
                dataIndex: 'metric',
                key: 'metric',
                fixed: 'left',
                width: 120,
                render: (text) => <Text strong style={{ fontSize: '11px' }}>{text}</Text>
            }
        ];

        // Цвета для выделения (как на примере пользователя)
        const planBgColor = '#fbe4d5'; // Оранжевый/персиковый для плана
        const factBgColor = '#d6e6f4'; // Синий/голубой для факта
        const planHeaderColor = '#f5a623'; // Оранжевый текст заголовка
        const factHeaderColor = '#1890ff'; // Синий текст заголовка

        sortedManagers.forEach((m, index) => {
            const isLast = index === sortedManagers.length - 1;
            cols.push({
                title: <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{getFirstName(m.name)}</span>,
                children: [
                    {
                        title: <span style={{ fontSize: '10px', color: planHeaderColor, fontWeight: 'bold' }}>ПЛАН</span>,
                        dataIndex: `${m.id}_plan`,
                        key: `${m.id}_plan`,
                        width: 80,
                        align: 'right',
                        onHeaderCell: () => ({
                            style: { backgroundColor: planBgColor }
                        }),
                        onCell: () => ({
                            style: { backgroundColor: planBgColor }
                        }),
                        render: (value, record) => {
                            if (value === null || value === undefined) return <span style={{ color: '#999' }}>-</span>;
                            if (record.isMoney) return <span style={{ fontSize: '10px' }}>{formatCurrency(value)}</span>;
                            if (record.isPercent) return <span style={{ fontSize: '10px' }}>{formatPercent(value)}</span>;
                            return <span style={{ fontSize: '10px' }}>{value}</span>;
                        }
                    },
                    {
                        title: <span style={{ fontSize: '10px', color: factHeaderColor, fontWeight: 'bold' }}>ФАКТ</span>,
                        dataIndex: `${m.id}_fact`,
                        key: `${m.id}_fact`,
                        width: 80,
                        align: 'right',
                        onHeaderCell: () => ({
                            style: {
                                backgroundColor: factBgColor,
                            }
                        }),
                        onCell: () => ({
                            style: {
                                backgroundColor: factBgColor,
                            }
                        }),
                        render: (value, record) => {
                            if (value === null || value === undefined) return <span style={{ color: '#999' }}>-</span>;
                            if (record.isMoney) return <span style={{ fontSize: '10px' }}>{formatCurrency(value)}</span>;
                            if (record.isPercent) return <span style={{ fontSize: '10px' }}>{formatPercent(value)}</span>;
                            return <span style={{ fontSize: '10px' }}>{value}</span>;
                        }
                    }
                ]
            });
        });

        return cols;
    }, [sortedManagers]);

    // Определяем заголовок периода из dateRange (должен быть до условного return для соблюдения правил хуков)
    const periodTitle = useMemo(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            const start = dateRange[0];
            const end = dateRange[1];
            const startMonth = start.month();
            const endMonth = end.month();
            const startYear = start.year();
            const endYear = end.year();

            if (startMonth === endMonth && startYear === endYear) {
                return `${MONTHS[startMonth]} ${startYear}`;
            } else {
                return `${start.format('DD.MM.YYYY')} – ${end.format('DD.MM.YYYY')}`;
            }
        }
        return `${MONTHS[moment().month()]} ${moment().year()}`;
    }, [dateRange]);

    if (!managers || managers.length === 0) {
        return <Alert message="Нет данных о менеджерах" type="info" />;
    }

    const hasHistoryData = Object.keys(historyData).length > 0;

    return (
        <Row gutter={[16, 16]}>
            {/* Текущий месяц - План/Факт */}
            <Col span={24}>
                <Spin spinning={loading}>
                    <Card
                        title={`Период: ${periodTitle}`}
                        size="small"
                        bodyStyle={{ padding: 0 }}
                    >
                        <Table
                            dataSource={currentMonthTableData}
                            columns={currentMonthColumns}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            bordered
                        />
                    </Card>
                </Spin>
            </Col>

            {/* Историческая динамика с выбором периода */}
            <Col span={24}>
                <Card
                    title="Историческая динамика по менеджерам"
                    size="small"
                    extra={
                        <Space>
                            <RangePicker
                                picker="month"
                                value={historyRange}
                                onChange={(dates) => setHistoryRange(dates)}
                                format="MMMM YYYY"
                                allowClear={false}
                                disabledDate={(current) => current && current.isBefore(MIN_DATA_DATE, 'month')}
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadHistoryData}
                                loading={historyLoading}
                            >
                                Обновить
                            </Button>
                        </Space>
                    }
                >
                    <Spin spinning={historyLoading}>
                        {hasHistoryData ? (
                            <>
                                <div style={{ marginBottom: 16 }}>
                                    <Select
                                        style={{ width: 200 }}
                                        placeholder="Выберите менеджера"
                                        value={selectedManagerId || (sortedManagers[0]?.id)}
                                        onChange={(value) => setSelectedManagerId(value)}
                                    >
                                        {sortedManagers.map(manager => (
                                            <Option key={manager.id} value={manager.id}>
                                                {getFirstName(manager.name)}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <Table
                                    dataSource={generateHistoricalRows(selectedManagerId || sortedManagers[0]?.id)}
                                    columns={historicalColumns}
                                    pagination={false}
                                    scroll={{ x: 'max-content' }}
                                    size="small"
                                    bordered
                                />
                            </>
                        ) : (
                            <Alert
                                type="info"
                                showIcon
                                message="Выберите период и нажмите 'Обновить' для загрузки данных"
                            />
                        )}
                    </Spin>
                </Card>
            </Col>
        </Row>
    );
};

export default ConversionReport;
